import React, { useEffect, useMemo, useState } from "react";
import { InputNumberFormat, format, unformat } from "@react-input/number-format";
import PaymentStepper from "../../../../component/pay/PaymentStepper";
import OnboardingLayout from "../../../../component/auth/OnboardingLayout";
import {
    ArrowDown2,
    ArrowRight2,
    Global,
    PercentageCircle,
    ReceiptDiscount,
    Timer1,
} from "iconsax-reactjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatMoney } from "../../../../utilities/formatMoney";
import currencyPairCheck from "../../../../utilities/currencyPairCheck";
import { CurrencySelector } from "../../../../component/wallet/CurrencySelector";
import useAccountBalance from "../../../../hooks/useAccountBalance";
import SelectNetworkModal from "../../../../component/wallet/SelectNetworkModal";
import {
    resetCryptoFiatTransfer,
    setCryptoFiatAmount,
    setCryptoFiatCurrency,
    setCryptoFiatNetwork,
    setCryptoFiatRecipientToCurrency,
} from "../../../../redux/slices/cryptoFiatTransferSlice";
import { fetchCoinNetworks, fetchCryptoRate, fetchTransferFees } from "../../../../api/fetchers";
import CurrencyToggle from "../../../../component/currencyToggle";
import { getApiMessage } from "../../../../utilities/getApiMessage";
import { resetCryptoTransfer } from "../../../../redux/slices/cryptoTransferSlice";
import { resetTransfer } from "../../../../redux/slices/tradevuTransferSlice";
import { resetBankTransfer } from "../../../../redux/slices/bankTransferSlice";
import { resetUsdTransfer } from "../../../../redux/slices/usdTransferSlice";
import { resetCrossBorderTransfer } from "../../../../redux/slices/crossBorderTransferSlice";
import InfoTooltip from "../../../../component/InfoTooltip";

const CryptoToFiatTransfer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // store
    const { balance, loading } = useAccountBalance();
    const { business, currency: currentCurrency } = useSelector((state) => state);
    const wallets = useSelector((state) => state.wallets || []);
    const businessId = business?._id;
    const isBusinessApproved = business?.status === "APPROVED";
    const toCurrencyFromStore = useSelector((s) => s.cryptoFiatTransfer?.recipient?.toCurrency);
    const networkFromStore = useSelector((s) => s.cryptoFiatTransfer?.network);

    // ui state
    const [amount, setAmount] = useState("");
    const [network, setNetwork] = useState(null);
    const [showNetworkModal, setShowNetworkModal] = useState(false);
    const [networks, setNetworks] = useState([]);
    const numericAmount = useMemo(() => {
        const n = Number.parseFloat(amount || "0");
        return Number.isFinite(n) ? n : 0;
    }, [amount]);

    // rate
    const [rate, setRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);
    const [rateError, setRateError] = useState(null);

    // fee & limits from backend
    const [feeLoading, setFeeLoading] = useState(false);
    const [txnFee, setTxnFee] = useState(0); // absolute fee (FROM = crypto) returned by backend
    const [minAmount, setMinAmount] = useState(0);
    const [maxAmount, setMaxAmount] = useState(0);
    const [dailyLimit, setDailyLimit] = useState(0);
    const [monthlyLimit, setMonthlyLimit] = useState(0);
    const [currentDaily, setCurrentDaily] = useState(0);
    const [currentMonthly, setCurrentMonthly] = useState(0);

    // fiat selection
    const [toCurrency, setToCurrency] = useState("");
    useEffect(() => {
        if (toCurrencyFromStore && !toCurrency) setToCurrency(toCurrencyFromStore);
    }, [toCurrencyFromStore, toCurrency]);

    // set FROM currency (crypto) in slice
    useEffect(() => {
        if (currentCurrency) {
            dispatch(setCryptoFiatCurrency(String(currentCurrency).toUpperCase()));
        }
    }, [currentCurrency, dispatch]);

    // reset other flows
    useEffect(() => {
        dispatch(resetBankTransfer());
        dispatch(resetUsdTransfer());
        dispatch(resetTransfer());
        dispatch(resetCryptoTransfer());
        dispatch(resetCryptoFiatTransfer());
        dispatch(resetCrossBorderTransfer());
    }, [dispatch]);

    // fetch rate (crypto -> fiat)
    useEffect(() => {
        const crypto = (currentCurrency || "").toUpperCase();
        const fiat = (toCurrency || "").toUpperCase();
        if (!crypto || !fiat) {
            setRate(null);
            setRateError(null);
            return;
        }

        setRateLoading(true);
        setRate(null);
        setRateError(null);

        fetchCryptoRate({ fiatCurrency: fiat, cryptoCurrency: crypto, businessId })
            .then((data) => {
                const r = Number(data?.rate ?? data?.value ?? data?.data ?? data);
                if (Number.isFinite(r) && r > 0) {
                    setRate(r);
                    setRateError(null);
                } else {
                    setRate(null);
                    const msg = getApiMessage(data) || "Invalid rate returned";
                    setRateError(msg);
                }
            })
            .catch((err) => {
                setRate(null);
                setRateError(getApiMessage(err) || "Failed to fetch rate");
            })
            .finally(() => setRateLoading(false));
    }, [toCurrency, currentCurrency, businessId]);

    useEffect(() => {
        if (!businessId || !currentCurrency || !toCurrency) return;

        let cancelled = false;
        (async () => {
            try {
                setFeeLoading(true);
                const res = await fetchTransferFees({
                    businessId,
                    params: {
                        currency: String(currentCurrency).toUpperCase(),
                        currencyTo: String(toCurrency).toUpperCase(),
                        amount: numericAmount || 0,
                    },
                });

                const d = res?.data ?? res ?? {};

                const feeAbs = Number(d.transactionFee) || 0;
                const min = Number(d.minimumTransferAmount) || 0;
                const max = Number(d.maximumTransferAmount) || 0;

                const dLimit = Number(d.dailyTransactionLimit) || 0;
                const mLimit = Number(d.monthlyTransactionLimit) || 0;
                const dUsed = Number(d.currentDailyTransfer) || 0;
                const mUsed = Number(d.currentMonthlyTransfer) || 0;

                if (!cancelled) {
                    setTxnFee(feeAbs);
                    setMinAmount(min);
                    setMaxAmount(max);
                    setDailyLimit(dLimit);
                    setMonthlyLimit(mLimit);
                    setCurrentDaily(dUsed);
                    setCurrentMonthly(mUsed);
                }
            } catch {
                if (!cancelled) {
                    setTxnFee(0);
                }
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [businessId, currentCurrency, toCurrency, numericAmount]);

    useEffect(() => {
        if (!currentCurrency || !businessId) return;

        let cancelled = false;
        (async () => {
            try {
                const list = await fetchCoinNetworks({
                    currency: String(currentCurrency).toUpperCase(),
                    businessId,
                });
                if (!cancelled) setNetworks(Array.isArray(list) ? list : []);
            } finally {
                // no-op
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentCurrency, businessId]);

    useEffect(() => {
        if (!networkFromStore || network || !networks.length) return;

        const storedKey = String(networkFromStore).toLowerCase();
        const matched = networks.find((item) => {
            const itemId = String(item?.id || "").toLowerCase();
            const itemName = String(item?.name || "").toLowerCase();
            const itemLabel = String(item?.label || item?.shortName || "").toLowerCase();
            return itemId === storedKey || itemName === storedKey || itemLabel === storedKey;
        });

        if (matched) setNetwork(matched);
    }, [networkFromStore, network, networks]);

    const NUMBER_FORMAT_OPTS = useMemo(
        () => ({
            locales: "en",
            maximumFractionDigits: 8, // adjust per rail if needed
        }),
        []
    );

    const formatted = useMemo(
        () => (amount ? format(amount, NUMBER_FORMAT_OPTS) : ""),
        [amount, NUMBER_FORMAT_OPTS]
    );

    const selectedNetworkBalance = useMemo(() => {
        if (!network) return null;

        const selectedKey = String(network?.id || network?.name || "").toLowerCase();
        const matched = networks.find((item) => {
            const itemId = String(item?.id || "").toLowerCase();
            const itemName = String(item?.name || "").toLowerCase();
            return itemId === selectedKey || itemName === selectedKey;
        });

        const value = Number(matched?.balance);
        return Number.isFinite(value) ? value : null;
    }, [network, networks]);

    // computed values
    const availableBalance =
        selectedNetworkBalance !== null ? selectedNetworkBalance : Number(balance || 0);
    const feeValue = Number(txnFee) || 0;
    const totalDebit = Math.max(0, numericAmount + feeValue);
    const insufficientFunds = totalDebit > availableBalance;

    // limits
    const dailyRemaining = Math.max(0, dailyLimit - currentDaily);
    const monthlyRemaining = Math.max(0, monthlyLimit - currentMonthly);
    const exceedDaily = dailyLimit > 0 && numericAmount > dailyRemaining;
    const exceedMonthly = monthlyLimit > 0 && numericAmount > monthlyRemaining;
    const WARN_PCT = 0.9;
    const nearDaily =
        dailyLimit > 0 &&
        !exceedDaily &&
        numericAmount > 0 &&
        (currentDaily + numericAmount) / dailyLimit >= WARN_PCT;
    const nearMonthly =
        monthlyLimit > 0 &&
        !exceedMonthly &&
        numericAmount > 0 &&
        (currentMonthly + numericAmount) / monthlyLimit >= WARN_PCT;

    // min/max
    const tooLow = numericAmount > 0 && numericAmount < (minAmount || 0);
    const tooHigh = maxAmount > 0 && numericAmount > maxAmount;

    const hasValidAmount =
        numericAmount > 0 &&
        !insufficientFunds &&
        !tooLow &&
        !tooHigh &&
        !exceedDaily &&
        !exceedMonthly;

    // recipient gets = (amount - fee) * rate
    const convertedAmount = useMemo(() => {
        const r = Number(rate || 0);
        if (!r || !hasValidAmount) return 0;
        const net = Math.max(0, numericAmount - feeValue);
        return net * r;
    }, [numericAmount, feeValue, rate, hasValidAmount]);

    const isRateReady =
        !rateLoading && typeof rate === "number" && Number.isFinite(rate) && rate > 0;
    const hasToCurrency = Boolean(toCurrency);
    const canContinue =
        hasValidAmount && hasToCurrency && isRateReady && !feeLoading && !rateError && !!network;

    const handleAmountChange = (e) => {
        const v = e.target.value;
        const raw = unformat(v || "", "en");

        setAmount(raw);
        dispatch(setCryptoFiatAmount(raw));
    };

    const handleContinue = () => {
        dispatch(setCryptoFiatCurrency(String(currentCurrency).toUpperCase()));
        dispatch(setCryptoFiatNetwork(network?.id || network?.name || ""));
        navigate("/payment/send-crypto/fiat-wallet/recipient-info");
    };

    return (
        <OnboardingLayout title="Send to bank" showSkip={false}>
            <div className="max-w-2xl mx-auto mt-10 md:px-4 space-y-10 pb-32 md:pb-0">
                <PaymentStepper currentStep={0} />
                <h2 className="text-2xl font-normal">How much do you want to send?</h2>

                <div className="bg-white rounded-xl p-6 max-w-3xl w-full">
                    {/* Top row */}
                    <div className="border-b pb-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-[14px] font-[450] text-[#383B56]">You send</span>
                            <CurrencyToggle
                                className="w-full text-sm bg-transparent"
                                dropDownClassName="shadow-dropdown2 right-0 !top-[109%]"
                                fetchWallet
                                businessId={businessId}
                                isDisabled={!isBusinessApproved}
                                hideLabel
                            />
                        </div>
                        <div
                            className={`flex items-start text-7xl font-normal leading-none ${
                                insufficientFunds ? "text-red-600" : ""
                            }`}
                        >
                            <InputNumberFormat
                                value={formatted}
                                onChange={handleAmountChange}
                                locales="en"
                                maximumFractionDigits={8}
                                type="text"
                                inputMode="decimal"
                                className={`!shadow-none outline-none font-thin w-full caret-[#6F00FF] ${
                                    insufficientFunds ? "border-b border-red-500" : ""
                                }`}
                                placeholder="0"
                                aria-invalid={insufficientFunds}
                                aria-describedby={insufficientFunds ? "amount-error" : undefined}
                            />
                        </div>
                        <div
                            className={`flex ${
                                insufficientFunds
                                    ? "justify-between"
                                    : "justify-between items-start"
                            }`}
                        >
                            <div className="text-sm space-y-1">
                                {exceedDaily && (
                                    <div role="alert" className="text-red-600">
                                        This amount exceeds your <b>daily limit</b>. Remaining
                                        today: {formatMoney(dailyRemaining)}{" "}
                                        {String(currentCurrency).toUpperCase()}.
                                    </div>
                                )}
                                {exceedMonthly && (
                                    <div role="alert" className="text-red-600">
                                        This amount exceeds your <b>monthly limit</b>. Remaining
                                        this month: {formatMoney(monthlyRemaining)}{" "}
                                        {String(currentCurrency).toUpperCase()}.
                                    </div>
                                )}
                                {insufficientFunds && (
                                    <div id="amount-error" role="alert" className="text-red-600">
                                        Insufficient balance. Available:{" "}
                                        {formatMoney(availableBalance)}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </div>
                                )}
                                {tooLow && (
                                    <div role="alert" className="text-red-600">
                                        Minimum transfer amount is {formatMoney(minAmount)}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </div>
                                )}
                                {tooHigh && (
                                    <div role="alert" className="text-red-600">
                                        Maximum transfer amount is {formatMoney(maxAmount)}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </div>
                                )}
                                {!exceedDaily &&
                                    !exceedMonthly &&
                                    !insufficientFunds &&
                                    !tooLow &&
                                    !tooHigh &&
                                    numericAmount > 0 && (
                                        <>
                                            {nearDaily && (
                                                <div className="text-amber-600">
                                                    Heads-up: this will put you close to your{" "}
                                                    <b>daily</b> limit. Remaining today:{" "}
                                                    {formatMoney(dailyRemaining)}{" "}
                                                    {String(currentCurrency).toUpperCase()}.
                                                </div>
                                            )}
                                            {nearMonthly && (
                                                <div className="text-amber-600">
                                                    Heads-up: this will put you close to your{" "}
                                                    <b>monthly</b> limit. Remaining this month:{" "}
                                                    {formatMoney(monthlyRemaining)}{" "}
                                                    {String(currentCurrency).toUpperCase()}.
                                                </div>
                                            )}
                                        </>
                                    )}
                            </div>

                            <div className="text-[#6B7280] text-sm">
                                {loading
                                    ? "Loading..."
                                    : `Balance: ${formatMoney(availableBalance)} ${String(
                                          currentCurrency
                                      ).toUpperCase()}`}
                                <InfoTooltip
                                    message={
                                        network
                                            ? `This is your ${currentCurrency} balance on ${network?.id || network?.name}`
                                            : `This is your total ${currentCurrency} balance`
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {/* Fee + Rate */}
                    <div className="relative my-6">
                        <div className="absolute top-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                        <div className="absolute bottom-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                        <div className="absolute left-4 top-[0px] bottom-[0px] w-px bg-[#E1E3F4] z-0" />
                        <span className="absolute left-[9px] top-[111px]">
                            <ArrowDown2 size={16} color="#E1E3F4" />
                        </span>

                        <div className="py-4 space-y-4">
                            {/* Fee */}
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                    <Global size={16} color="#6F739B" variant="Bulk" />
                                </div>
                                <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                    <span>Send from</span>
                                    <button
                                        type="button"
                                        onClick={() => setShowNetworkModal(true)}
                                        className="text-[#7A27FF] font-[450] text-[16px] flex items-center gap-1"
                                    >
                                        {network
                                            ? `${network.name || network.id || "—"} (${network.label || network.shortName || "—"})`
                                            : "Select network"}
                                        <ArrowRight2 size={18} />
                                    </button>
                                </div>
                            </div>
                            {/* Fee */}
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                    <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                </div>
                                <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                    <span>Fee</span>
                                    <span>
                                        {feeLoading
                                            ? "Fetching fee…"
                                            : `${formatMoney(feeValue)} ${currencyPairCheck(
                                                  currentCurrency
                                              )}`}
                                    </span>
                                </div>
                            </div>

                            {/* Rate */}
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                    <PercentageCircle size={16} color="#6F739B" variant="Bulk" />
                                </div>
                                <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                    <span>Rate</span>
                                    <span>
                                        {toCurrency ? (
                                            rateLoading ? (
                                                "Fetching rate…"
                                            ) : rateError ? (
                                                <span className="text-red-600" role="alert">
                                                    {rateError}
                                                </span>
                                            ) : (
                                                `1 ${String(
                                                    currentCurrency
                                                ).toUpperCase()} = ${rate} ${String(
                                                    toCurrency
                                                ).toUpperCase()}`
                                            )
                                        ) : (
                                            "Select a currency"
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recipient gets + selector */}
                    <div className="pt-4 border-t">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-500">Recipient gets</span>
                            <CurrencySelector
                                wallets={wallets.filter((w) => w.currency !== currentCurrency)}
                                selected={toCurrency}
                                onSelect={(code) => {
                                    setToCurrency(code);
                                    dispatch(setCryptoFiatRecipientToCurrency(code));
                                }}
                                currencyFrom={String(currentCurrency || "").toUpperCase()}
                            />
                        </div>

                        <div className="flex items-start text-7xl font-thin leading-none">
                            <span>{formatMoney(convertedAmount)}</span>
                        </div>
                    </div>
                </div>

                {/* Desktop footer */}
                <div className="max-w-2xl mx-auto mb-5 hidden md:block">
                    <div className="flex justify-between items-center border-t border-[#E5E7EB]">
                        <div className="flex items-center gap-2 text-[#6B7280] text-sm">
                            <Timer1 size="22" color="#6F739B" />
                            <span>Arrives in minutes</span>
                        </div>

                        <div className="flex items-center gap-4">
                            <button
                                className="text-[#111827] text-[16px] font-[450]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                disabled={!canContinue}
                                aria-busy={rateLoading || feeLoading}
                                className={`rounded-xl px-6 py-2 text-white font-semibold text-[16px] ${
                                    !canContinue
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF] hover:bg-[#6A1BFF]"
                                }`}
                                onClick={handleContinue}
                                title={
                                    !network
                                        ? "Select network"
                                        : !toCurrency
                                          ? "Select a currency"
                                          : rateLoading
                                            ? "Waiting for rate…"
                                            : feeLoading
                                              ? "Calculating fee…"
                                              : tooLow
                                                ? `Minimum is ${formatMoney(minAmount)} ${String(
                                                      currentCurrency
                                                  ).toUpperCase()}`
                                                : tooHigh
                                                  ? `Maximum is ${formatMoney(maxAmount)} ${String(
                                                        currentCurrency
                                                    ).toUpperCase()}`
                                                  : exceedDaily
                                                    ? `Exceeds daily limit. Remaining: ${formatMoney(
                                                          dailyRemaining
                                                      )} ${String(currentCurrency).toUpperCase()}`
                                                    : exceedMonthly
                                                      ? `Exceeds monthly limit. Remaining: ${formatMoney(
                                                            monthlyRemaining
                                                        )} ${String(currentCurrency).toUpperCase()}`
                                                      : !hasValidAmount
                                                        ? "Enter a valid amount"
                                                        : ""
                                }
                            >
                                {rateLoading || feeLoading ? "Please wait…" : "Continue"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-2 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text-[15px] font-[450]">Arrives in minutes</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                className="text-[#111827] text-[16px] font-[450] w-[48%]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>

                            <button
                                disabled={!canContinue}
                                aria-busy={rateLoading || feeLoading}
                                onClick={handleContinue}
                                className={`h-12 rounded-xl px-5 font-semibold text-white text-[16px] w-[48%] ${
                                    !canContinue
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF]"
                                }`}
                                title={
                                    !network
                                        ? "Select network"
                                        : !toCurrency
                                          ? "Select a currency"
                                          : rateLoading
                                            ? "Waiting for rate…"
                                            : feeLoading
                                              ? "Calculating fee…"
                                              : tooLow
                                                ? `Minimum is ${formatMoney(minAmount)} ${String(
                                                      currentCurrency
                                                  ).toUpperCase()}`
                                                : tooHigh
                                                  ? `Maximum is ${formatMoney(maxAmount)} ${String(
                                                        currentCurrency
                                                    ).toUpperCase()}`
                                                  : exceedDaily
                                                    ? `Exceeds daily limit. Remaining: ${formatMoney(
                                                          dailyRemaining
                                                      )} ${String(currentCurrency).toUpperCase()}`
                                                    : exceedMonthly
                                                      ? `Exceeds monthly limit. Remaining: ${formatMoney(
                                                            monthlyRemaining
                                                        )} ${String(currentCurrency).toUpperCase()}`
                                                      : !hasValidAmount
                                                        ? "Enter a valid amount"
                                                        : ""
                                }
                            >
                                {rateLoading || feeLoading ? "Please wait…" : "Continue"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <SelectNetworkModal
                isOpen={showNetworkModal}
                onClose={() => setShowNetworkModal(false)}
                onSelect={(net) => {
                    setNetwork(net);
                    setShowNetworkModal(false);
                }}
                selectedId={network?.id}
                currency={currentCurrency}
                businessId={businessId}
            />
        </OnboardingLayout>
    );
};

export default CryptoToFiatTransfer;
